import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        // 更新 state 使下一次渲染能够显示降级后的 UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // 记录错误信息
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // 这里可以将错误信息发送到错误报告服务
        // reportErrorToService(error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    render() {
        if (this.state.hasError) {
            // 自定义降级 UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const errorBoundaryStyles: React.CSSProperties = {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                padding: '20px',
                backgroundColor: '#f5f5f5',
            };

            const errorContainerStyles: React.CSSProperties = {
                maxWidth: '500px',
                padding: '30px',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                textAlign: 'center',
            };

            const errorTitleStyles: React.CSSProperties = {
                color: '#e74c3c',
                marginBottom: '16px',
            };

            const errorTextStyles: React.CSSProperties = {
                color: '#666',
                marginBottom: '24px',
                lineHeight: '1.5',
            };

            const errorActionsStyles: React.CSSProperties = {
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                marginBottom: '20px',
            };

            const buttonBaseStyles: React.CSSProperties = {
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'background-color 0.2s',
            };

            const retryButtonStyles: React.CSSProperties = {
                ...buttonBaseStyles,
                backgroundColor: '#3498db',
                color: 'white',
            };

            const reloadButtonStyles: React.CSSProperties = {
                ...buttonBaseStyles,
                backgroundColor: '#95a5a6',
                color: 'white',
            };

            const errorDetailsStyles: React.CSSProperties = {
                textAlign: 'left',
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
            };

            const errorStackStyles: React.CSSProperties = {
                fontSize: '12px',
                color: '#666',
                whiteSpace: 'pre-wrap',
                overflowX: 'auto',
            };

            return (
                <div style={errorBoundaryStyles}>
                    <div style={errorContainerStyles}>
                        <h2 style={errorTitleStyles}>出现了一些问题</h2>
                        <p style={errorTextStyles}>
                            应用程序遇到了意外错误。请尝试刷新页面或联系技术支持。
                        </p>

                        <div style={errorActionsStyles}>
                            <button
                                onClick={this.handleRetry}
                                style={retryButtonStyles}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#2980b9';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '#3498db';
                                }}
                            >
                                重试
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                style={reloadButtonStyles}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.backgroundColor = '#7f8c8d';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.backgroundColor = '#95a5a6';
                                }}
                            >
                                刷新页面
                            </button>
                        </div>

                        {process.env.NODE_ENV === 'development' && (
                            <details style={errorDetailsStyles}>
                                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                                    错误详情（开发模式）
                                </summary>
                                <pre style={errorStackStyles}>
                                    {this.state.error && this.state.error.toString()}
                                    <br />
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// 简化版错误边界组件
export const SimpleErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
    return (
        <ErrorBoundary
            fallback={
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#e74c3c'
                }}>
                    <h3>出现错误</h3>
                    <p>请刷新页面重试</p>
                </div>
            }
        >
            {children}
        </ErrorBoundary>
    );
};